import { useAppSelector } from 'appState/store'
import RobotSmillingIcon from 'assets/images/icons/RobotSmillingIcon.svg?react'
import cx from 'classnames'
import { LoadingBox } from '@outbound-ai/ursa'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FaEdit, FaPlus } from 'react-icons/fa'
import { Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ColorsEnum, ColorsToneEnum, PositionEnum } from 'types/cssTypes'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { notificator } from 'utils/notificator/notificator'
import { v4 } from 'uuid'

import { settingsServiceApiPaths, setUserPreference } from '../settingsApi'
import { useUserPreference } from '../settingsHooks'
import {
  DEFAULT_PAYER_VA_SOFTPHONE_KEY,
  IPhonePreferences,
  IUserPhone,
  MAX_NUMBER_SOFTPHONE,
  UserPreferencesKeysEnum,
} from '../settingsTypes'
import { parseJSON } from '../settingsUtils'
import { SettingsAddEditPhoneModal } from './SettingsAddEditPhoneModal'

enum PhoneActionEnum {
  add,
  edit,
  selection,
}

const phoneActionNotificationMessage = (action: PhoneActionEnum) => {
  switch (action) {
    case PhoneActionEnum.add:
      return `New phone successfully added`
    case PhoneActionEnum.edit:
      return `Phone edited successfully`
    case PhoneActionEnum.selection:
      return `Default phone changed successfully`
  }
}

const isEmptyPhoneValue = (id: string, phones: IUserPhone[]) => {
  return id !== DEFAULT_PAYER_VA_SOFTPHONE_KEY && isEmpty(phones.find((phone) => phone.id === id)?.value)
}
export const SettingsCalls = () => {
  // api
  const {
    data: userPhoneData,
    error: userPhoneError,
    isLoading,
    mutate,
  } = useUserPreference(UserPreferencesKeysEnum.softphonePhoneNumbers)

  // local state
  const [config, setConfig] = useState<IPhonePreferences>({
    currentSelection: DEFAULT_PAYER_VA_SOFTPHONE_KEY,
    phones: [],
  })
  const [isUpdateSettingsLoading, setIsUpdateSettingsLoading] = useState(false)
  const { activeCallJobId } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const isCallInProgress = activeCallJobId ? true : false

  useEffect(() => {
    if (userPhoneData?.value) {
      const parsedPhoneData = parseJSON(userPhoneData.value)
      if (!isEmpty(parsedPhoneData) && typeof parsedPhoneData === 'object') {
        const phones = (parsedPhoneData as IPhonePreferences).phones?.map((phone) => {
          if (!phone.value) phone.value = ''
          if (!phone.id) phone.id = v4()
          return phone
        })
        setConfig({ ...(parsedPhoneData as IPhonePreferences), phones })
      }
    }
  }, [userPhoneData, userPhoneError])

  useEffect(() => {
    userPhoneError &&
      notificator.notify({
        heading: userPhoneError.message || ErrorMessagesEnum.ResourceNotFound,
        type: 'error',
      }) //notificator.error(userPhoneError.message || ErrorMessagesEnum.ResourceNotFound)
  }, [userPhoneError])

  const handleAddPhone = async (newPhone: IUserPhone) => {
    setConfig((config) => {
      // will not destructure config.phones since the max number of allowed phones is 1 - MAX_NUMBER_SOFTPHONE
      const newConfig = { ...config, currentSelection: newPhone.id, phones: [newPhone] }
      onUpdateSettings(newConfig, PhoneActionEnum.add)

      return newConfig
    })
  }

  const handleEditPhone = async (newPhone: IUserPhone) => {
    setConfig((config) => {
      const newConfig = {
        ...config,
        phones: config.phones.map((phone) => (phone.id === newPhone.id ? newPhone : phone)),
      }
      onUpdateSettings(newConfig, PhoneActionEnum.edit)

      return newConfig
    })
  }

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target as HTMLInputElement

    if (isEmptyPhoneValue(id, config.phones)) return
    setConfig((config) => {
      onUpdateSettings({ ...config, currentSelection: id }, PhoneActionEnum.selection)
      return { ...config, currentSelection: id }
    })
  }

  const onUpdateSettings = async (config: IPhonePreferences, action: PhoneActionEnum) => {
    try {
      setIsUpdateSettingsLoading(true)
      await setUserPreference(settingsServiceApiPaths.userPreferencesPut(), {
        key: UserPreferencesKeysEnum.softphonePhoneNumbers,
        value: JSON.stringify(config),
      })
      await mutate()
      notificator.notify({
        heading: phoneActionNotificationMessage(action),
        type: 'success',
      })
      //notificator.success(phoneActionNotificationMessage(action))
    } catch (e: any) {
      notificator.notify({
        heading: e.message || ErrorMessagesEnum.SomethingWentWrong,
        type: 'error',
      })
      //notificator.error(e.message || ErrorMessagesEnum.SomethingWentWrong)
    } finally {
      setIsUpdateSettingsLoading(false)
    }
  }

  const browserSoftphone: IUserPhone = { id: DEFAULT_PAYER_VA_SOFTPHONE_KEY, name: 'Claims Work softphone', value: 'x' }
  const FormCheck = (currentSelection: string, phone: IUserPhone) => {
    return (
      <Form.Check
        type="radio"
        checked={currentSelection === phone.id}
        key={phone.id}
        id={phone.id}
        name={phone.name}
        onChange={handleRadioChange}
        disabled={isCallInProgress}
        label={
          <>
            <span
              className={cx('d-flex align-items-center gap-2', {
                'opacity-50': isEmptyPhoneValue(phone.id, config.phones),
              })}
            >
              <span className="default-phone-name">{phone.name}</span>
              {phone.id !== DEFAULT_PAYER_VA_SOFTPHONE_KEY && !isEmpty(phone.value) && (
                <span>
                  <span className="opacity-25 inline-block mx-2">|</span>
                  {phone.value}
                </span>
              )}
              {currentSelection === phone.id && (
                <Tooltip
                  className="robot-tooltip"
                  position={PositionEnum.right}
                  positionTooltipArrow={15}
                  tooltipText="The Claims Work built-in softphone is always used by the AI Agent to initiate, record and transcribe payer calls."
                >
                  <div className="default-icon">
                    <RobotSmillingIcon width="20" height="20" />
                  </div>
                </Tooltip>
              )}
            </span>
            <span>
              {phone.id !== DEFAULT_PAYER_VA_SOFTPHONE_KEY && (
                <SettingsAddEditPhoneModal
                  title={
                    isEmpty(phone.value) ? (
                      <>
                        <FaPlus /> Add phone details
                      </>
                    ) : (
                      <>
                        <FaEdit /> Edit Phone Details
                      </>
                    )
                  }
                  handleAddPhone={handleEditPhone}
                  phoneDetails={phone}
                  isCallInProgress={isCallInProgress}
                />
              )}
            </span>
          </>
        }
      />
    )
  }

  return (
    <Div>
      {isLoading && <LoadingBox />}

      {!isLoading && (
        <div className="phones">
          <>
            <div className="section-header">
              <h2>Phones</h2>
              <p>Select the phone you would like to use to participate in payer calls.</p>
            </div>

            <Form className={cx('position-relative', { inactive: isUpdateSettingsLoading })}>
              <>
                {isUpdateSettingsLoading && <LoadingBox />}

                {FormCheck(config.currentSelection, browserSoftphone)}
                {config.phones.map((phone) => FormCheck(config.currentSelection, phone))}
                {config.phones.length < MAX_NUMBER_SOFTPHONE && (
                  <section className="mt-1">
                    <SettingsAddEditPhoneModal
                      title={
                        <>
                          <FaPlus /> Add phone details
                        </>
                      }
                      handleAddPhone={handleAddPhone}
                    />
                  </section>
                )}
              </>
            </Form>
          </>
        </div>
      )}
    </Div>
  )
}

const Div = styled.div`
  max-width: ${({ theme }) => theme.util.pixelToRem(610)};
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 10rem;
  position: relative;

  > .phones {
    .section-header {
      color: ${({ theme }) => theme.colors.grey.darker};
      h2 {
        font-size: ${({ theme }) => theme.util.pixelToRem(18)};
      }
      p {
        font-size: ${({ theme }) => theme.util.pixelToRem(13)};
      }
    }
    form {
      > .form-check {
        padding: 0.675rem;
        padding-left: 2rem;
        display: flex;
        align-items: center;
        gap: 0.675rem;
        border-bottom: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};

        .form-check-input {
          margin-top: -0.25rem;
        }

        .form-check-input[disabled],
        .form-check-input[disabled] + label {
          opacity: 0.3;
        }

        label {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          ${({ theme }) => theme.typography.s.normal};

          .default-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;

            svg path {
              fill: ${({ theme }) => theme.colors.primary.light};
            }
          }
          .robot-tooltip .tooltip-container {
            left: 3rem;
            top: 0;
            max-width: 12rem;
            width: 12rem;
            background-color: ${({ theme }) => theme.colors.grey.lightest};
            color: ${({ theme }) => theme.colors.grey.darker};
            .tooltip-arrow::after {
              border-color: transparent ${({ theme }) => theme.colors.grey.lightest} transparent transparent;
            }
          }
          .default-phone-name {
            color: ${({ theme }) => theme.colors.grey.darker};
          }
        }
      }
    }

    button {
      display: flex;
      align-items: center;
      font-weight: ${({ theme }) => theme.fontWeight.normal};

      svg {
        position: relative;
        top: -0.125rem;
      }
    }
    .add-phone-container {
      button {
        ${({ theme }) => theme.typography.s.normal};
        &:hover {
          background-color: ${({ theme }) => theme.colors.info.lightest};
        }
      }
    }
  }
`
